import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  ApiClientError,
  apiClient,
  isAuthenticationRequired,
  type SessionView,
  type UserSummary
} from "../api/client.js";

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | {
      status: "authenticated";
      user: UserSummary;
      csrfToken: string;
    };

interface AuthContextValue {
  state: AuthState;
  notice: string | undefined;
  login(username: string, password: string): Promise<UserSummary>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  continueWithLogin(notice: string): void;
  logout(): Promise<void>;
  refreshSession(): Promise<void>;
  recoverAuthentication(error: unknown): boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
let sessionRefreshInFlight: Promise<SessionView> | undefined;

export class PasswordChangedLoginRequiredError extends Error {
  constructor() {
    super("Password changed, but the replacement session could not be created.");
    this.name = "PasswordChangedLoginRequiredError";
  }
}

function requestSession(): Promise<SessionView> {
  sessionRefreshInFlight ??= apiClient.getSession().finally(() => {
    sessionRefreshInFlight = undefined;
  });
  return sessionRefreshInFlight;
}

function authenticated(session: SessionView): AuthState {
  return {
    status: "authenticated",
    user: session.user,
    csrfToken: session.csrfToken
  };
}

async function requestSessionWithConflictRetry(): Promise<SessionView> {
  try {
    return await requestSession();
  } catch (error) {
    if (
      error instanceof ApiClientError &&
      error.code === "SESSION_REFRESH_CONFLICT"
    ) {
      return requestSession();
    }
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [notice, setNotice] = useState<string>();

  const recoverAuthentication = useCallback((error: unknown): boolean => {
    if (!isAuthenticationRequired(error)) return false;
    apiClient.setCsrfToken(undefined);
    setNotice("登录已失效，请重新登录。");
    setState({ status: "anonymous" });
    return true;
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      setState(authenticated(await requestSessionWithConflictRetry()));
    } catch (error) {
      recoverAuthentication(error);
      throw error;
    }
  }, [recoverAuthentication]);

  useEffect(() => {
    let active = true;
    void requestSessionWithConflictRetry()
      .then((session) => {
        if (active) setState(authenticated(session));
      })
      .catch(() => {
        if (active) {
          apiClient.setCsrfToken(undefined);
          setState({ status: "anonymous" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      notice,
      async login(username, password) {
        const session = await apiClient.login({ username, password });
        setNotice(undefined);
        setState(authenticated(session));
        return session.user;
      },
      async changePassword(currentPassword, newPassword) {
        if (state.status !== "authenticated") {
          throw new Error("当前没有可更新的登录会话。");
        }
        const username = state.user.username;
        try {
          await apiClient.changePassword({ currentPassword, newPassword });
        } catch (error) {
          recoverAuthentication(error);
          throw error;
        }
        setState({ status: "loading" });
        try {
          const replacementSession = await apiClient.login({
            username,
            password: newPassword
          });
          setState(authenticated(replacementSession));
        } catch {
          apiClient.setCsrfToken(undefined);
          throw new PasswordChangedLoginRequiredError();
        }
      },
      continueWithLogin(nextNotice) {
        apiClient.setCsrfToken(undefined);
        setNotice(nextNotice);
        setState({ status: "anonymous" });
      },
      async logout() {
        try {
          await apiClient.logout();
        } catch (error) {
          if (recoverAuthentication(error)) {
            setNotice(undefined);
            return;
          }
          setNotice("退出登录失败，请重试。");
          throw error;
        }
        setNotice(undefined);
        setState({ status: "anonymous" });
      },
      refreshSession,
      recoverAuthentication
    }),
    [notice, recoverAuthentication, refreshSession, state]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return context;
}
